import React from 'react';
import { generatePath, useHistory } from 'react-router-dom';

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { From, Message } from 'flow/types/Chats';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';
import { extractName } from 'flow/utils';
import { twMerge } from 'tailwind-merge';
import FromAvatar from './FromAvatar';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';

interface Props {
    message: TypedMap<Message>;
    sessionKey: ChatSessionKey;
}

const ChatPostLikedList = ({ message, sessionKey }: Props) => {
    const { i18next } = useAppContext();
    const history = useHistory();
    const { closeModal } = useModal();

    const likedBy = message.get('likedBy');
    const likeCount = likedBy?.count() || 0;

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const onClick = (liker: TypedMap<From>) => {
        const extensionKey = liker?.get('extensionKey');
        if (extensionKey !== ownExtensionKey) {
            closeModal();
            history.push(
                generatePath(routes['messages.id.settings.contact.key'], {
                    extensionKey: extensionKey,
                    id: sessionKey,
                }),
                { from: history.location.pathname },
            );
        }
    };

    return (
        <Popover className="relative">
            <PopoverButton className="focus-ring">
                {
                    <span className="text-main text-sm font-medium">
                        {i18next.t('chat.post.likes.like_count', {
                            likeCount,
                        })}
                    </span>
                }
            </PopoverButton>
            {likeCount === 0 ? null : (
                <PopoverPanel className="absolute bottom-full z-40 mb-2 bg-white shadow-lg">
                    <ul className="max-h-[30dvh] overflow-y-auto p-2">
                        {likedBy.map((liker) => (
                            <li
                                key={liker.get('extensionKey')}
                                className={twMerge(
                                    'flex items-center gap-x-2 p-1.5',
                                    liker?.get('extensionKey') !==
                                        ownExtensionKey
                                        ? 'cursor-pointer'
                                        : null,
                                )}
                                onClick={() => onClick(liker)}
                            >
                                <FromAvatar
                                    from={liker}
                                    avatarOnly={true}
                                    size="small"
                                    chatSessionKey={sessionKey}
                                />
                                <div className="flex-1 whitespace-nowrap">
                                    {extractName(
                                        liker.get('contact'),
                                        i18next.t('Contact.Unknown'),
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                </PopoverPanel>
            )}
        </Popover>
    );
};

export default ChatPostLikedList;
